import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {combineLatest, from, Observable} from 'rxjs';
import {filter, first, map, switchMap} from 'rxjs/operators';

import {
    addDoc,
    collection,
    collectionData,
    doc,
    docData,
    DocumentReference,
    Firestore,
    getDoc,
    getDocs,
    orderBy,
    query,
    runTransaction,
    updateDoc,
    where
} from '@angular/fire/firestore';

import {AuthService} from './auth.service';
import {ConfigService} from './config.service';
import {calculateCustomerFromHostname} from '../util';

import {Order} from '../interfaces/order';
import {Sequence} from '../interfaces/sequence';
import {AclUser} from '../interfaces/app-user';
import {OrderLIstFilter} from './order-list-table.service';



const ORDER_COLLECTION = 'Order';
const SEQ_COLLECTION = 'Sequences';

@Injectable({providedIn: 'root'})
export class OrderService {
    private readonly cDR: DocumentReference<any>;

    private auth: AuthService = inject(AuthService);
    private firestore: Firestore = inject(Firestore);
    private configService: ConfigService = inject(ConfigService);
    private http: HttpClient = inject(HttpClient);

    constructor() {
        const CUSTOMER_DOMAIN = calculateCustomerFromHostname(document.location.hostname);
        this.cDR = doc(this.firestore, 'Customers', CUSTOMER_DOMAIN);
    }

    public prepareOrderPdf(orderId: string): Observable<any> {
        const param = {orderId: orderId, domainId: this.auth.appDomain};
        return this.auth.getUserIdToken().pipe(
            first(),
            switchMap((tid) => {
                const sett = this.configService.settings.value;
                const endPoint = sett.value.firebaseApi + '/order/createpdf';
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tid}`,
                    'X-App-Domain': this.auth.appDomain
                });
                if (tid) {
                    return this.http.post(endPoint, param, {
                        headers: headers
                    });
                } else {
                    throw Error('No token');
                }
            })
        );
    }

    public askOrderAuth(order: Order): Observable<any> {
        const param = {order: order};
        return this.auth.getUserIdToken().pipe(
            first(),
            switchMap((tid) => {
                const sett = this.configService.settings.value;
                const endPoint = sett.value.firebaseApi + '/order/askOrderAuth';
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tid}`,
                    'X-App-Domain': this.auth.appDomain
                });
                if (tid) {
                    return this.http.post(endPoint, param, {
                        headers: headers
                    });
                } else {
                    throw Error('No token');
                }
            })
        );
    }

    public ackOrderAuth(order: Order, isAuth: boolean): Observable<any> {
        const param = {order: order, isAuth: isAuth};
        return this.auth.getUserIdToken().pipe(
            first(),
            switchMap((tid) => {
                const sett = this.configService.settings.value;
                const endPoint = sett.value.firebaseApi + '/order/ackOrderAuth';
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tid}`,
                    'X-App-Domain': this.auth.appDomain
                });
                if (tid) {
                    return this.http.post(endPoint, param, {
                        headers: headers
                    });
                } else {
                    throw Error('No token');
                }
            })
        );
    }

    public getOrdersByBudget = (budgetId: string) => {
        if (!budgetId) {
            throw Error('Budget Id is not defined');
        }

        return combineLatest([
            this.auth.getUserIdToken().pipe(first()),
            this.configService.settings.pipe(
                filter((value) => value != null), first())
        ]).pipe(
            first(),
            switchMap(([tid, value]) => {
                const endPoint = value.value.firebaseApi + '/material/orders/' + budgetId;
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tid}`,
                    'X-App-Domain': this.auth.appDomain
                });
                if (tid) {
                    return this.http.get<any[]>(endPoint, {
                        responseType: 'json',
                        headers: headers
                    });
                } else {
                    throw Error('No token');
                }
            })
        );
    }

    public patchOrder = (bdgtId: string, data: Partial<Order>) => {
        const itemDoc = doc(this.cDR, ORDER_COLLECTION, bdgtId);
        return updateDoc(itemDoc, data);
    }

    public sendNdaReminder = (recipients: any, vendor: any) => {
        const endPoint = this.settingsApiUrl() + '/order/sendNdaReminder';

        return this.auth.getUserIdToken().pipe(
            first(),
            switchMap((tid) => {
                if (tid) {
                    const headers = new HttpHeaders({
                        'Authorization': `Bearer ${tid}`,
                        'X-App-Domain': this.auth.appDomain,
                        'Access-Control-Allow-Origin': '*'
                    });
                    return this.http.post(endPoint,
                        {
                            recipients,
                            vendor,
                            userFrom: this.auth.currentUser?.email
                        },
                        {headers: headers}
                    );
                } else {
                    throw Error('No token');
                }
            })
        );
    }

    public getNextOrderNr = () => {
        const orderSeq = doc(this.cDR, SEQ_COLLECTION, 'Order');
        const docRef = docData(orderSeq) as Observable<Sequence>;
        return docRef.pipe(map((seq: Sequence) => {
            return seq.value + 1;
        }));
    }

    public getOrderList = () => {
        const q = query(collection(this.cDR, ORDER_COLLECTION), where('deleted', '==', false), orderBy('date', 'desc'));
        return collectionData(q, {idField: 'id'}) as Observable<Order[]>;
    }

    public getQuickOrderList() {
        const q = query(collection(this.cDR, ORDER_COLLECTION), where('deleted', '==', false), where('searchMode', '==', 'quick'));

        return from(getDocs(q)).pipe(map(d => {
            return d.docs
                .map(o => ({...o.data(), id: o.id}))
                .map((order => this.calculateOrderTotalInvoiceAmount(order as Order)));
        }));
    }

    public getOrderListPaginated(pageSize: number, startAt: number, filterQuery: OrderLIstFilter) {
        return combineLatest([this.auth.getUserIdToken(), this.configService.settings.pipe(filter(s => s !== null))]).pipe(
            first(),
            switchMap(([tid, settings]) => {
                const endPoint = settings?.value.firebaseApi + '/order/paginatedList';
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tid}`,
                    'X-App-Domain': this.auth.appDomain
                });
                if (tid) {
                    return this.http.post<{ data: any[], totalSize: number }>(endPoint, {
                        pageSize,
                        startAt,
                        filter: filterQuery
                    }, {
                        headers: headers
                    });
                } else {
                    throw Error('No token');
                }
            })
        );
    }

    public getOrderListOnce = () => {
        const q = query(collection(this.cDR, ORDER_COLLECTION), where('deleted', '==', false), orderBy('date', 'desc'));
        return getDocs(q);
    }

    public markOrderAsDelivered = (orderId: string, delivered: any, realDeliveryDate: any) => {
        return combineLatest([this.auth.getUserIdToken(), this.configService.settings.pipe(filter(s => s !== null))]).pipe(
            first(),
            switchMap(([tid, settings]) => {
                const endPoint = settings?.value.firebaseApi + '/order/markAsDelivered';
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tid}`,
                    'X-App-Domain': this.auth.appDomain
                });
                if (tid) {
                    return this.http.post<{ message: string }>(endPoint, {
                        orderId: orderId,
                        delivered: delivered,
                        realDeliveryDate: realDeliveryDate
                    }, {
                        headers: headers
                    });
                } else {
                    throw Error('No token');
                }
            })
        );
    }

    public getLimitedOrderListOnce = () => {
        const q = query(collection(this.cDR, ORDER_COLLECTION), where('deleted', '==', false), where('searchMode', '==', 'quick'), orderBy('date', 'desc'));
        return getDocs(q);
    }

    public getOrder = (orderId: string) => {
        const orderRef = doc(this.cDR, ORDER_COLLECTION, orderId);
        return docData(orderRef) as Observable<Order>;
    }

    public getSingleOrder = (orderId: string) => {
        const orderRef = doc(this.cDR, ORDER_COLLECTION, orderId);
        return getDoc(orderRef);
    }

    public addOrder = (order: Order) => {
        console.log(order);
        return runTransaction(this.firestore, async (transaction) => {
            const CUSTOMER_DOMAIN = calculateCustomerFromHostname(document.location.hostname);
            const referenceUsingUnderlyingLibrary = doc(this.firestore, 'Customers', CUSTOMER_DOMAIN, SEQ_COLLECTION, 'Order');

            const orderSeq: any = await transaction.get(referenceUsingUnderlyingLibrary);
            const newOrderNum = orderSeq.data().value + 1;
            transaction.update(referenceUsingUnderlyingLibrary, {value: newOrderNum});
            order.orderNr = this.buildOrderNr(newOrderNum, order.author);
            order.deleted = false;
            const orderCollRef = collection(this.cDR, ORDER_COLLECTION);
            return addDoc(orderCollRef, order as any);
        });
    }

    public deleteOrder = (order: Order) => {
        const orderRef = doc(this.cDR, ORDER_COLLECTION, order.id);
        return updateDoc(orderRef, {
            deleted: true,
            deletedOn: new Date(),
            deletedBy: this.auth.currentUser?.googleUid
        });
    }

    public updateOrder = (orderId: string, order: Partial<Order>) => {
        console.log(orderId, order);
        const orderRef = doc(this.cDR, ORDER_COLLECTION, orderId);
        return updateDoc(orderRef, order);
    }

    /** LASER CUT */
    public getLaserCutNesting = () => {
        const nestingCollRef = collection(this.cDR, 'LaserCut/root/Nesting');
        return collectionData(nestingCollRef);
    }

    private settingsApiUrl(): string {
        return this.configService.settings.value?.value?.firebaseApi || '';
    }

    private buildOrderNr = (num: number, user: Partial<AclUser>) => {
        const paddedNum = num.toString().padStart(4, '0');
        const year = (new Date()).getFullYear();
        return user.initials + 'OF ' + paddedNum + '-' + year;
    }

    private calculateOrderTotalInvoiceAmount(order: Order): Order {
        if (order.totalValue && typeof (order.totalValue) === 'string') {
            order.totalValue = Number(order.totalValue);
        }

        let totalInvoiceAmount = 0;
        for (const item of order.items) {
            if (item.invoices) {
                for (const invoice of item.invoices) {
                    totalInvoiceAmount += invoice.materialAmount;
                }
            }
        }

        order.orderInvoiceCompletion = order.totalValue ? ((1 / order.totalValue) * totalInvoiceAmount) : 1;
        (order as any)['invoiceRemaining'] = order.totalValue ? order.totalValue - totalInvoiceAmount : 0;

        return order;
    }
}